import { z } from 'zod'
import { geoPointSchema } from './geo.dto.js'

/**
 * What is around the trees: roads, buildings and water, drawn under them on a
 * proposal's site plan.
 *
 * Fetched once from OpenStreetMap when a site's boundary is drawn, stored on
 * the site, and copied into the proposal document like the boundary. That is
 * what lets the plan be drawn in the brand's own colors as flat shapes, print
 * as sharply as the type around it, and stay exactly as it was on a proposal
 * already sent, with no tile server in the way.
 *
 * Points are `[latitude, longitude]` pairs rather than the `{ latitude,
 * longitude }` objects used elsewhere. A site can carry a few hundred building
 * outlines, every sent version stores its own copy, and the keys would be most
 * of the bytes.
 */
export const featurePointSchema = z.tuple([
  geoPointSchema.shape.latitude,
  geoPointSchema.shape.longitude,
])

export type FeaturePoint = z.infer<typeof featurePointSchema>

/**
 * How many features a site plan will store. The OpenStreetMap mapping trims
 * to these same numbers, so it cannot build a document the schema then
 * refuses whole.
 */
export const SITE_FEATURE_LIMITS = {
  buildings: 1500,
  water: 300,
  roads: 1500,
  ringPoints: 400,
  roadPoints: 800,
} as const

const ringSchema = z
  .array(featurePointSchema)
  .min(3)
  .max(SITE_FEATURE_LIMITS.ringPoints)

/**
 * How a road is drawn. `major` is a through street, `minor` a residential
 * street or service drive, `path` a sidewalk or trail.
 */
export const ROAD_KINDS = ['major', 'minor', 'path'] as const

export const roadKindSchema = z.enum(ROAD_KINDS)

export type RoadKind = z.infer<typeof roadKindSchema>

/** Drawn back to front: paths under streets, so a major road paints over a trail. */
export const ROAD_DRAW_ORDER: readonly RoadKind[] = [...ROAD_KINDS].reverse()

export const siteFeaturesSchema = z.object({
  buildings: z.array(ringSchema).max(SITE_FEATURE_LIMITS.buildings),
  /** Ponds, lakes and pools alike: all are drawn in the water colors. */
  water: z.array(ringSchema).max(SITE_FEATURE_LIMITS.water),
  roads: z
    .array(
      z.object({
        kind: roadKindSchema,
        name: z.string().nullable(),
        points: z
          .array(featurePointSchema)
          .min(2)
          .max(SITE_FEATURE_LIMITS.roadPoints),
      }),
    )
    .max(SITE_FEATURE_LIMITS.roads),
  /** When the features were fetched, as an ISO timestamp. */
  fetchedAt: z.string().datetime(),
})

export type SiteFeatures = z.infer<typeof siteFeaturesSchema>
